import React from 'react';

import { Box, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material';
import { orange, red } from '@mui/material/colors';

import { formatAudCurrency } from '~/utils/numberFormatters';

import MarginWarningCell from './MarginWarningCell';
import { MIN_MARGIN_PCT, calcMargin, isGrossBelowBase, isMarginBelowMinimum, netToAud } from './marginRules';
import {
  BASIC_CATEGORY_INDEX,
  CELL_SX,
  EXTRA_NIGHT_DIVIDER_SX,
  GROUP_DIVIDER_SX,
  HEADER_SX,
  PACKAGE_CATEGORIES,
  groupStartSx,
  stickyCellSx,
} from './packageCategories';
import { type CategoryRoom, type PackageOptionId, type RoomPricing, roomDisplayName } from './types';

const LOS_COL_WIDTH = 110;
const ROOM_TYPE_LABEL_COL_WIDTH = 60;
const ROOM_TYPE_NAME_COL_WIDTH = 220;

// Math.round(-0.4) is -0, which Intl prints as "-$0".
const wholeAud = (value: number) => formatAudCurrency(Math.round(value) || 0);

type MarginTableProps = {
  audToSourceCurrencyRate: Record<string, number | undefined>;
  netCurrency: string;
  optionLabel: string;
  pricingByRoomId: Record<PackageOptionId, RoomPricing>;
  roomsByCategory: Array<Array<CategoryRoom>>;
  sortedLos: Array<number>;
};

export default function MarginTable({
  audToSourceCurrencyRate,
  netCurrency,
  optionLabel,
  pricingByRoomId,
  roomsByCategory,
  sortedLos,
}: MarginTableProps) {
  const toAud = (net: number) => netToAud(net, netCurrency, audToSourceCurrencyRate);

  // Non-basic packages compare against the basic package for the same asset id - never the
  // name, since names are not unique on an Account. takenRoomAssetKeys keeps base assets
  // unique, but if two ever share an id the first wins, matching PricingTable's allocation lookup.
  const basicGrossByAsset: Record<
    string,
    { extraNight: number | undefined; byLos: Record<number, number | undefined> }
  > = {};
  roomsByCategory[BASIC_CATEGORY_INDEX].forEach(({ id, assetId }) => {
    if (!assetId || basicGrossByAsset[assetId]) {
      return;
    }
    const pricing = pricingByRoomId[id];
    const byLos: Record<number, number | undefined> = {};
    sortedLos.forEach((nights) => {
      byLos[nights] = calcMargin(
        Number(pricing?.sellByLos[nights]) || 0,
        toAud(Number(pricing?.netByLos[nights]) || 0),
      )?.gross;
    });
    basicGrossByAsset[assetId] = {
      byLos,
      extraNight: calcMargin(Number(pricing?.extraNightSell) || 0, toAud(Number(pricing?.extraNightNet) || 0))?.gross,
    };
  });

  const grossColWidth = sortedLos.length === 1 ? 190 : LOS_COL_WIDTH;
  const marginTableWidth =
    ROOM_TYPE_LABEL_COL_WIDTH +
    ROOM_TYPE_NAME_COL_WIDTH +
    sortedLos.length * grossColWidth +
    sortedLos.length * LOS_COL_WIDTH +
    130 +
    110;

  return (
    <Paper sx={{ mt: 4, p: 2 }} variant="outlined">
      <Stack spacing={2}>
        <Box>
          <Typography fontWeight={700} variant="h6">
            Margins, {optionLabel}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Worked out from the sell and net prices above. Red is below the {MIN_MARGIN_PCT}% minimum; amber is a
            package earning less than its base room.
          </Typography>
        </Box>
        <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto', width: 'auto' }}>
          <Table size="small" sx={{ tableLayout: 'fixed', width: marginTableWidth }}>
            <colgroup>
              <col style={{ width: ROOM_TYPE_LABEL_COL_WIDTH }} />
              <col style={{ width: ROOM_TYPE_NAME_COL_WIDTH }} />
              {sortedLos.map((nights) => (
                <col key={`gross-col-${nights}`} style={{ width: grossColWidth }} />
              ))}
              {sortedLos.map((nights) => (
                <col key={`margin-col-${nights}`} style={{ width: LOS_COL_WIDTH }} />
              ))}
              <col style={{ width: 130 }} />
              <col style={{ width: 110 }} />
            </colgroup>
            <TableBody>
              <TableRow>
                <TableCell colSpan={2} sx={{ ...stickyCellSx(0) }} />
                <TableCell
                  align="center"
                  colSpan={sortedLos.length}
                  sx={{ ...HEADER_SX, ...GROUP_DIVIDER_SX, whiteSpace: 'nowrap' }}
                >
                  Gross margin
                </TableCell>
                <TableCell align="center" colSpan={sortedLos.length} sx={{ ...HEADER_SX, ...GROUP_DIVIDER_SX }}>
                  Margin %
                </TableCell>
                <TableCell
                  align="center"
                  colSpan={2}
                  sx={{ ...HEADER_SX, ...EXTRA_NIGHT_DIVIDER_SX, color: orange[700] }}
                >
                  Extra night
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={2} sx={{ ...HEADER_SX, ...stickyCellSx(0) }}>
                  Room type
                </TableCell>
                {sortedLos.map((nights, index) => (
                  <TableCell align="center" key={`gross-${nights}`} sx={{ ...HEADER_SX, ...groupStartSx(index) }}>
                    {nights}N
                  </TableCell>
                ))}
                {sortedLos.map((nights, index) => (
                  <TableCell align="center" key={`margin-${nights}`} sx={{ ...HEADER_SX, ...groupStartSx(index) }}>
                    {nights}N
                  </TableCell>
                ))}
                <TableCell align="center" sx={{ ...HEADER_SX, ...EXTRA_NIGHT_DIVIDER_SX, whiteSpace: 'nowrap' }}>
                  Gross (AUD)
                </TableCell>
                <TableCell align="center" sx={{ ...HEADER_SX, whiteSpace: 'nowrap' }}>
                  Margin %
                </TableCell>
              </TableRow>
              {PACKAGE_CATEGORIES.map((category, categoryIndex) => {
                const rooms = roomsByCategory[categoryIndex];
                if (categoryIndex !== BASIC_CATEGORY_INDEX && rooms.every((room) => !room.assetName)) {
                  return null;
                }
                const rowCellSx = { ...CELL_SX, bgcolor: category.bgColor };
                const labelSx = {
                  ...stickyCellSx(0, category.bgColor),
                  color: category.labelColor,
                  transform: 'rotate(180deg)',
                  writingMode: 'vertical-lr' as const,
                };

                return rooms.map(({ id, assetId, assetName, suffix }, index) => {
                  const pricing = pricingByRoomId[id];
                  const basicGross = categoryIndex === BASIC_CATEGORY_INDEX ? undefined : basicGrossByAsset[assetId];

                  return (
                    <TableRow key={id}>
                      {index === 0 && (
                        <TableCell align="center" rowSpan={rooms.length} sx={labelSx}>
                          {category.label}
                        </TableCell>
                      )}
                      <TableCell sx={{ ...stickyCellSx(ROOM_TYPE_LABEL_COL_WIDTH, category.bgColor) }}>
                        {roomDisplayName({ assetName, suffix }) || '—'}
                      </TableCell>
                      {sortedLos.map((nights, losIndex) => {
                        const sell = Number(pricing?.sellByLos[nights]) || 0;
                        const net = toAud(Number(pricing?.netByLos[nights]) || 0);
                        const margin = calcMargin(sell, net);
                        const baseGross = basicGross?.byLos[nights];
                        const belowBasic = margin !== undefined && isGrossBelowBase(sell, margin.gross, baseGross);
                        return (
                          <MarginWarningCell
                            color={orange[800]}
                            key={`gross-value-${nights}`}
                            reason={
                              belowBasic && baseGross !== undefined
                                ? `Gross margin is below the base package gross margin (${wholeAud(baseGross)}) for ${nights}N`
                                : null
                            }
                            sx={{ ...rowCellSx, ...groupStartSx(losIndex) }}
                            value={margin === undefined ? '—' : wholeAud(margin.gross)}
                          />
                        );
                      })}
                      {sortedLos.map((nights, losIndex) => {
                        const sell = Number(pricing?.sellByLos[nights]) || 0;
                        const net = toAud(Number(pricing?.netByLos[nights]) || 0);
                        const margin = calcMargin(sell, net);
                        return (
                          <MarginWarningCell
                            color={red[700]}
                            key={`margin-value-${nights}`}
                            reason={
                              isMarginBelowMinimum(sell, margin?.pct)
                                ? `Margin is below the ${MIN_MARGIN_PCT}% minimum`
                                : null
                            }
                            sx={{ ...rowCellSx, ...groupStartSx(losIndex) }}
                            value={margin === undefined ? '—' : `${margin.pct.toFixed(1)}%`}
                          />
                        );
                      })}
                      {(() => {
                        const extraSell = Number(pricing?.extraNightSell) || 0;
                        const extraNet = toAud(Number(pricing?.extraNightNet) || 0);
                        const margin = calcMargin(extraSell, extraNet);
                        const baseExtraGross = basicGross?.extraNight;
                        const belowBasic =
                          margin !== undefined && isGrossBelowBase(extraSell, margin.gross, baseExtraGross);
                        return (
                          <>
                            <MarginWarningCell
                              color={orange[800]}
                              reason={
                                belowBasic && baseExtraGross !== undefined
                                  ? `Extra night gross margin is below the base package extra night gross margin (${wholeAud(baseExtraGross)})`
                                  : null
                              }
                              sx={{ ...rowCellSx, ...EXTRA_NIGHT_DIVIDER_SX }}
                              value={margin === undefined ? '—' : wholeAud(margin.gross)}
                            />
                            <MarginWarningCell
                              color={red[700]}
                              reason={
                                isMarginBelowMinimum(extraSell, margin?.pct)
                                  ? `Margin is below the ${MIN_MARGIN_PCT}% minimum`
                                  : null
                              }
                              sx={rowCellSx}
                              value={margin === undefined ? '—' : `${margin.pct.toFixed(1)}%`}
                            />
                          </>
                        );
                      })()}
                    </TableRow>
                  );
                });
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </Paper>
  );
}
